import { useState } from "react";
import { Newspaper, Download, FileImage, FileText, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

/**
 * Compact download-e-paper widget for any article page.
 * Generates a beautiful Dainik-Bhaskar-style JPG or PDF for the given news slug.
 */
export default function EPaperDownloadWidget({ slug, title }) {
  const [busy, setBusy] = useState("");
  const [showMenu, setShowMenu] = useState(false);

  const grab = async (fmt) => {
    if (!slug) return;
    setBusy(fmt);
    try {
      const url = `${API}/epaper/single?slug=${encodeURIComponent(slug)}&fmt=${fmt}`;
      // Trigger native browser download
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error("Server error");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      const safe = (title || slug).replace(/[^a-zA-Z0-9\u0900-\u097F -]/g, "").slice(0, 40) || "epaper";
      link.download = `INDIAN-NEWS-21_${safe}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`✅ E-Paper ${fmt.toUpperCase()} download हो गया!`);
      setShowMenu(false);
    } catch (e) {
      toast.error("E-Paper generate नहीं हो सका, कुछ देर बाद try करें");
    } finally { setBusy(""); }
  };

  return (
    <div className="relative inline-block" data-testid="epaper-widget">
      <button
        type="button"
        onClick={() => setShowMenu((v) => !v)}
        className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-[#DC2626] via-[#EA580C] to-[#F59E0B] hover:from-[#B91C1C] hover:to-[#DC2626] text-white text-sm font-black px-4 py-2.5 shadow-md hover:shadow-lg transition"
        data-testid="epaper-btn"
      >
        <Newspaper className="w-4 h-4"/>📰 E-Paper Download
      </button>
      {showMenu && (
        <div className="absolute top-full mt-2 right-0 z-40 w-64 rounded-xl bg-white shadow-2xl border-2 border-slate-100 overflow-hidden" data-testid="epaper-menu">
          <div className="px-4 py-3 bg-slate-900 text-white">
            <div className="text-[10px] uppercase tracking-widest font-black text-amber-300">Newspaper-Style Download</div>
            <div className="text-sm font-bold">HD Quality · Dainik Bhaskar Style</div>
          </div>
          <button
            onClick={() => grab("jpg")}
            disabled={busy === "jpg"}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition text-left border-b border-slate-100"
            data-testid="epaper-jpg"
          >
            <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center text-orange-600">
              {busy === "jpg" ? <Loader2 className="w-5 h-5 animate-spin"/> : <FileImage className="w-5 h-5"/>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black text-sm text-slate-800">JPG Image</div>
              <div className="text-[11px] text-slate-500">2400×3200 · HD · WhatsApp share करने योग्य</div>
            </div>
            <Download className="w-4 h-4 text-slate-400"/>
          </button>
          <button
            onClick={() => grab("pdf")}
            disabled={busy === "pdf"}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition text-left"
            data-testid="epaper-pdf"
          >
            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center text-red-600">
              {busy === "pdf" ? <Loader2 className="w-5 h-5 animate-spin"/> : <FileText className="w-5 h-5"/>}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-black text-sm text-slate-800">PDF File</div>
              <div className="text-[11px] text-slate-500">Print-ready · Archive के लिए बेहतर</div>
            </div>
            <Download className="w-4 h-4 text-slate-400"/>
          </button>
        </div>
      )}
    </div>
  );
}
